import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Checkbox, Form, Popconfirm } from 'antd';
import { Rule } from 'antd/es/form';
import { DeleteOutlined } from '@ant-design/icons';
import cx from 'classnames';
import { FieldData } from 'rc-field-form/es/interface';
import {
    IAntDFormChangeFieldName,
    IEditFormComponentInfo,
    IFormFields,
    IGetApiType,
    NotificationType,
} from '../../../interfaces';
import { useCardInfo, useProfileIsRight, useRouterStore } from '../../../hooks';
import {
    addNotification,
    apiMessagesDeleteMessageByFieldName,
    apiMessagesGetFormRules,
    apiMessagesSaveMessages,
    deleteCardWithSubscribers,
    noSemicolonsOrCommasRule,
    patchCard,
    rulesIsNumber,
} from '../../../middleware';
import { cleanPathFields } from '../../../instruments';
import { tB, tF, tI } from '../../../translate';
import {
    CardInfoToolBar,
    CardMainLayout,
    Catcher,
    FieldDataVolumeUnit,
    InputDebounce,
    TextAreaDebounce,
    cardLayoutOptions,
} from '../../../Components';
import Styles from '../../styles/m_itemCardStyles.less';

export const EditCardInfo: React.FC<IEditFormComponentInfo> = observer(({ setEditMode }: IEditFormComponentInfo) => {
    const [isSending, setSending] = useState(false);
    const routerStore = useRouterStore();
    const isLord = useProfileIsRight('LORD');
    const isAdmin = useProfileIsRight('ADMIN');
    const componentSpinners: IGetApiType[] = ['GET_CARD'];
    const [form] = Form.useForm();
    const { isHashed, ...initialValues } = useCardInfo();

    const isNew = !initialValues.id;

    const handleDelete = async (): Promise<void> => {
        const resp = await deleteCardWithSubscribers(initialValues.id);

        if (resp.isError) {
            addNotification({
                type: NotificationType.ERROR,
                message: 'An error has occurred while deleting the card',
                isUsersError: true,
            });
        }

        routerStore.goTo('CardList');
    };

    const checkValid = (): void => {
        form.validateFields();
    };

    useEffect(() => {
        form.setFieldsValue({
            ...initialValues,
        });
        checkValid();
    }, [JSON.stringify(initialValues)]);

    const onFieldsChange = (changedFields: FieldData[]): void => {
        const fieldName = changedFields && changedFields[0] && (changedFields[0].name as IAntDFormChangeFieldName);

        if (fieldName) {
            apiMessagesDeleteMessageByFieldName(fieldName);
        }
        if (!form.getFieldValue('imei') && !!form.getFieldValue('lockImei')) {
            form.setFieldsValue({ lockImei: false });
        }
    };

    const onFinish = async (fields: IFormFields): Promise<void> => {
        setSending(true);
        const cleanFields = cleanPathFields(fields, initialValues, ['usageLimit', 'usageLimitUnit']);
        const { isError, errors } = await patchCard(initialValues.id, cleanFields);
        if (isError) {
            apiMessagesSaveMessages(errors);
            checkValid();
        } else {
            setEditMode && setEditMode(false);
        }
        setSending(false);
    };

    const validateNumber: Array<Rule> = [rulesIsNumber, ...apiMessagesGetFormRules];
    const validateSemicolonOrComma: Array<Rule> = [noSemicolonsOrCommasRule, ...apiMessagesGetFormRules];

    const nameTitle = tF('Name');
    const printedId = tF('Printed ID');
    const nameEID = tF('EID');
    const nameIMEI = tF('IMEI');
    const nameNotes = tF('Notes');
    const nameBatchId = tF('Batch Id');

    return (
        <Catcher>
            <CardMainLayout
                columnOption={cardLayoutOptions.cardInfo}
                idSpinners={componentSpinners}
                isEdit
                name={initialValues.name}
            >
                <Form
                    form={form}
                    name="cardInfo"
                    initialValues={initialValues}
                    onFieldsChange={onFieldsChange}
                    onFinish={onFinish}
                    scrollToFirstError={true}
                    layout={'vertical'}
                >
                    <Form.Item name="name" label={nameTitle} rules={validateSemicolonOrComma}>
                        <InputDebounce placeholder={nameTitle} isFirstInput disabled={isHashed} delay={0} />
                    </Form.Item>
                    {isLord ? (
                        <Form.Item name="plmnList" label={tF('PLMN List')} rules={apiMessagesGetFormRules}>
                            <InputDebounce placeholder="PLMN" />
                        </Form.Item>
                    ) : null}
                    {isLord ? (
                        <Form.Item name="batchId" label={nameBatchId} rules={validateNumber}>
                            <InputDebounce placeholder={nameBatchId} delay={0} />
                        </Form.Item>
                    ) : null}
                    {isAdmin ? (
                        <>
                            <Form.Item name="printedId" label={printedId} rules={apiMessagesGetFormRules}>
                                <InputDebounce placeholder={printedId} delay={0} />
                            </Form.Item>
                            <Form.Item name="eid" label={nameEID} rules={apiMessagesGetFormRules}>
                                <InputDebounce placeholder={nameEID} delay={0} />
                            </Form.Item>
                            <Form.Item name="imei" label={nameIMEI} rules={apiMessagesGetFormRules}>
                                <InputDebounce placeholder={nameIMEI} delay={0} />
                            </Form.Item>
                            <Form.Item name="lockImei" valuePropName="checked" rules={apiMessagesGetFormRules}>
                                <Checkbox>{tF('Locked IMEI')}</Checkbox>
                            </Form.Item>
                            {(isLord || (isAdmin && initialValues.limitChangeAllowed)) && (
                                <FieldDataVolumeUnit
                                    fields={{ limit: 'usageLimitInUnit', unit: 'usageLimitUnit' }}
                                    form={form}
                                    initialValues={initialValues}
                                    label={'Card data limit per month'}
                                />
                            )}
                            <Form.Item name="notes" label={nameNotes} rules={apiMessagesGetFormRules}>
                                <TextAreaDebounce placeholder={nameNotes} autoSize={true} delay={0} />
                            </Form.Item>
                        </>
                    ) : null}
                    <CardInfoToolBar isLoading={isSending} isNew={isNew} isSubmitButton>
                        {isLord ? (
                            <Popconfirm
                                title={tI(
                                    'Deleting this card will delete all IMSIs (subscribers) attached to it from the system. Do you want to proceed?',
                                )}
                                onConfirm={handleDelete}
                                okText={tB('Yes')}
                                cancelText={tB('No')}
                                placement="bottom"
                            >
                                <Button
                                    type="primary"
                                    icon={<DeleteOutlined />}
                                    danger
                                    className={cx(Styles.mainButton)}
                                >
                                    {tB('Delete')}
                                </Button>
                            </Popconfirm>
                        ) : null}
                    </CardInfoToolBar>
                </Form>
            </CardMainLayout>
        </Catcher>
    );
});
